import { useContext, useEffect } from 'react';

import { StockBuySellContext } from '@context/stock/buysell';
import useRealtimeStockPrice from '@context/websocket/useRealtimeStockPrice';
import stockService from '@services/stock';

const getMaxBuy = (buyingPower, orderDetail) => {
  if (orderDetail && orderDetail.order_type === 'LIMIT') {
    // For Amend Buy Limit order case, include funds currently locked in the current order
    return buyingPower + (orderDetail?.total || 0);
  }
  // For New Buy order case, return as it is
  // For Amend Buy Auto order case, return as it is
  return buyingPower;
};

const getMaxSell = (availableLot, orderDetail) => {
  if (orderDetail && orderDetail.order_type === 'LIMIT') {
    // For Amend Sell Limit order case, include lot currently locked in the current order
    return availableLot + (orderDetail?.lot_count || 0);
  }
  // For New Sell order case, return as it is
  // For Amend Sell Auto order case, return as it is
  return availableLot;
};

const useInitFetchApi = (code, { orderDetail } = {}) => {
  const { formKey, formAction, dispatch } = useContext(StockBuySellContext);

  const { data: stockPrice } = useRealtimeStockPrice(code);

  const { isTradeable: isDtTradeable } = stockService.detail.dt.tradeable.useDtTradeable(code);
  const { isTradeable: isMtTradeable } = stockService.detail.mt.tradeable.useMtTradeable(code);
  const { buyingPower, usableBuyingPower } =
    stockService.account.regular.tradelimit.useTradelimit();
  const { dtBuyingPower, usableDtBuyingPower } = stockService.account.dt.tradelimit.useDtTradelimit(
    {
      code,
      enabled: isDtTradeable, // Only call API when stock is DT tradeable
    }
  );
  const { mtBuyingPower, usableMtBuyingPower } = stockService.account.mt.tradelimit.useMtTradelimit(
    {
      code,
      enabled: isMtTradeable, // Only call API when stock is MT tradeable
    }
  );
  const { availableLot } = stockService.account.regular.portfolio.usePortfolioByCode(code);
  const { dtAvailableLot } = stockService.account.dt.portfolio.useDtPortfolioByCode(code);
  const { mtAvailableLot } = stockService.account.mt.portfolio.useMtPortfolioByCode(code);

  useEffect(() => {
    if (!code) return;

    // Update form on API call return
    dispatch({
      type: formAction.SET_CONST_FROM_API,
      code,
      payload: {
        // Trade limit for calculation purpose: BE trade limit (usable funds) - transaction max fee
        [formKey.CONST_MAX_BUY]: getMaxBuy(usableBuyingPower, orderDetail),
        [formKey.CONST_MAX_BUY_DT]: getMaxBuy(usableDtBuyingPower, orderDetail),
        [formKey.CONST_MAX_BUY_MT]: getMaxBuy(usableMtBuyingPower, orderDetail),
        // Trade limit as seen by user: BE trade limit
        [formKey.CONST_MAX_BUY_LABEL]: getMaxBuy(buyingPower, orderDetail),
        [formKey.CONST_MAX_BUY_LABEL_DT]: getMaxBuy(dtBuyingPower, orderDetail),
        [formKey.CONST_MAX_BUY_LABEL_MT]: getMaxBuy(mtBuyingPower, orderDetail),

        // Available lot as seen by user: lot that is available for sell (lots that are already on open sell order cannot be sold again)
        // Available lot for calculation purposes: same as what is seen by user
        [formKey.CONST_MAX_SELL]: getMaxSell(availableLot, orderDetail),
        [formKey.CONST_MAX_SELL_DT]: getMaxSell(dtAvailableLot, orderDetail),
        [formKey.CONST_MAX_SELL_MT]: getMaxSell(mtAvailableLot, orderDetail),
      },
    });
  }, [
    code,
    buyingPower,
    usableBuyingPower,
    dtBuyingPower,
    usableDtBuyingPower,
    mtBuyingPower,
    usableMtBuyingPower,
    availableLot,
    dtAvailableLot,
    mtAvailableLot,
    orderDetail,
  ]);

  useEffect(() => {
    // Update form on API call return and on Websocket update
    dispatch({
      type: formAction.SET_CONST_FROM_API,
      code,
      payload: {
        [formKey.CONST_DEFAULT_PRICE]: stockPrice?.price,
      },
    });
  }, [code, stockPrice]);

  return {};
};

export default useInitFetchApi;
